import { useState } from "react";
import {
  useAdminSettings,
  usePricingAction,
  useUpdateSettings,
} from "@/features/admin/admin";
import { FormSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

// One membership, and only one. `supported` and `sponsor` were removed from the
// backend on 2026-08-17 — they were never purchasable — and this page kept
// reading `variants.supported.enabled` off undefined, which is what made
// Settings crash rather than render. The card names `standard` directly now
// rather than mapping a list of one.

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  const id = `toggle-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <Label htmlFor={id}>{label}</Label>
        {description && (
          <p className="text-muted-foreground mt-0.5 text-xs">{description}</p>
        )}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/**
 * One price, shown as money and edited in dollars.
 *
 * There were four edit affordances on this card and three verbs for the same
 * act — "Re-price", "Update", "Update fee" — each an unlabelled box with a
 * placeholder standing in for a label, and each price also written into the
 * text of the switch beside it. A switch says whether a thing is on sale; it
 * should not also be where its price is published.
 *
 * The Save button appears only once the number has changed, which is how the
 * closed-applications message on this page already behaves.
 */
function PriceField({
  label,
  cents,
  suffix,
  action,
  onSave,
}: {
  label: string;
  cents: number;
  suffix?: string;
  action: ReturnType<typeof usePricingAction>;
  onSave: (cents: number) => void;
}) {
  const asDollars = (c: number) => (c / 100).toFixed(2);
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? asDollars(cents);
  const parsed = Number.parseFloat(value);
  const changed = draft !== null && Number.isFinite(parsed)
    && Math.round(parsed * 100) !== cents;
  const id = `price-${label.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <Label htmlFor={id} className="w-40 shrink-0 font-normal">
        {label}
      </Label>
      <div className="relative">
        <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-sm">
          $
        </span>
        <Input
          id={id}
          inputMode="decimal"
          className="w-28 pl-5 tabular-nums"
          value={value}
          onChange={(e) => setDraft(e.target.value)}
        />
      </div>
      {suffix && (
        <span className="text-muted-foreground text-sm">{suffix}</span>
      )}
      {changed && (
        <Button
          size="sm"
          disabled={action.isPending}
          onClick={() =>
            onSave(Math.round(parsed * 100))
          }
        >
          Save
        </Button>
      )}
      {action.error && (
        <span className="text-destructive text-xs">{action.error.message}</span>
      )}
    </div>
  );
}

/** What is on sale, as distinct from what it costs. */
function OfferRow({
  label,
  description,
  checked,
  onChange,
  children,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  children?: React.ReactNode;
}) {
  const id = `offer-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="space-y-3 py-3 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Label htmlFor={id}>{label}</Label>
          <p className="text-muted-foreground mt-0.5 text-sm">{description}</p>
        </div>
        <Switch id={id} checked={checked} onCheckedChange={onChange} />
      </div>
      {checked && children}
    </div>
  );
}

export default function SettingsPage() {
  const { data: settings, isPending, error } = useAdminSettings();
  const update = useUpdateSettings();
  const reprice = usePricingAction("variant-price");
  const donationPrice = usePricingAction("donation-price");
  const feePrice = usePricingAction("processing-fee");
  const [closedMessage, setClosedMessage] = useState<string | null>(null);

  if (isPending) return <FormSkeleton />;
  if (error) return <p className="text-destructive text-sm">{error.message}</p>;
  if (!settings) return null;

  const toggle = (key: string) => (value: boolean) =>
    update.mutate({ [key]: value });
  const message = closedMessage ?? settings.applicationsClosedMessage;
  const donation = settings.pricing.variants.donation;
  const standard = settings.pricing.variants.standard;

  return (
    <div className="max-w-3xl space-y-4 pb-8">
      <div>
        <h1 className="text-2xl">Settings</h1>
        {/* What is left here is what an administrator actually changes. The
            page also carried auto-approval, admin notifications, the email
            sender, the support address and a moderation switch for a board
            that left scope — settings touched once at a cutover, or never.
            They live in /basement/ with the audit log and the outbox, which is
            where the rest of the rarely-used admin already is. */}
        <p className="text-muted-foreground mt-1 text-sm">
          Effective immediately, no deploy needed. Every change is audit-logged.
        </p>
      </div>

      {update.error && (
        <p className="text-destructive text-sm">{update.error.message}</p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>Applications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            label="Application intake open"
            description="Off replaces the registration form with the notice below."
            checked={settings.applicationsOpen}
            onChange={toggle("applicationsOpen")}
          />
          {/* Only while it is closed. This is not a third setting, it is the
              wording of the one above, and a textarea for a notice nobody is
              being shown is a box to scroll past. */}
          {!settings.applicationsOpen && (
          <div className="space-y-2">
            <Label htmlFor="closed-message">What visitors see instead</Label>
            <Textarea
              id="closed-message"
              rows={3}
              value={message}
              onChange={(e) => setClosedMessage(e.target.value)}
            />
            {message !== settings.applicationsClosedMessage && (
              <Button
                size="sm"
                onClick={() =>
                  update.mutate(
                    { applicationsClosedMessage: message },
                    { onSuccess: () => setClosedMessage(null) }
                  )
                }
              >
                Save message
              </Button>
            )}
          </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <CardTitle className={sectionTitleClass}>Pricing</CardTitle>
            {!settings.stripeConfigured && (
              <Badge variant="outline" className="text-amber-600 dark:text-amber-500">
                Stripe not configured
              </Badge>
            )}
          </div>
          <CardDescription>
            {settings.stripeConfigured
              ? "Re-pricing creates a new Stripe price. Existing subscribers keep the one they signed up at."
              : "There is no Stripe key in this environment, so nothing here can be charged yet. The figures are still what the site quotes."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* What it costs. The card used to publish these inside the labels
              of the switches beside them, so a switch said whether a thing was
              on sale and also what it cost. */}
          <div className="space-y-3">
            <PriceField
              label="Membership"
              cents={standard.membershipCents ?? 0}
              suffix="a year"
              action={reprice}
              onSave={(membership_cents) =>
                reprice.mutate({ variant: "standard", membership_cents })
              }
            />
            <PriceField
              label="Service fee"
              cents={settings.pricing.processingFeeCents}
              suffix="a year, once per transaction"
              action={feePrice}
              onSave={(processing_fee_cents) =>
                feePrice.mutate({ processing_fee_cents })
              }
            />
          </div>

          <div className="divide-y border-t pt-2">
            <OfferRow
              label="Free membership"
              description="A directory listing, the listserv and the resource archive."
              checked={settings.pricing.variants.free.enabled}
              onChange={(enabled) =>
                update.mutate({ variantEnabled: { variant: "free", enabled } })
              }
            />
            <OfferRow
              label="Paid membership"
              description="Full access to the directory and the library."
              checked={standard.enabled}
              onChange={(enabled) =>
                update.mutate({ variantEnabled: { variant: "standard", enabled } })
              }
            />
            <OfferRow
              label="Donations"
              description="Offered on the billing page, on top of any membership."
              checked={donation.enabled}
              onChange={(enabled) =>
                update.mutate({ variantEnabled: { variant: "donation", enabled } })
              }
            >
              <div className="space-y-3 pl-1">
                <PriceField
                  label="Suggested"
                  cents={donation.suggestedCents ?? 0}
                  action={donationPrice}
                  onSave={(suggested_cents) =>
                    donationPrice.mutate({
                      suggested_cents,
                      minimum_cents: donation.minimumCents ?? 0,
                    })
                  }
                />
                <PriceField
                  label="Minimum"
                  cents={donation.minimumCents ?? 0}
                  action={donationPrice}
                  onSave={(minimum_cents) =>
                    donationPrice.mutate({
                      suggested_cents: donation.suggestedCents ?? 0,
                      minimum_cents,
                    })
                  }
                />
              </div>
            </OfferRow>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
